import getFirebase from "../../../api/firebase";
import type { UserProfile } from "./types";

export const loginWithGoogle = async (): Promise<firebase.default.User> => {
  const firebase = await getFirebase();
  const provider = new firebase.auth.GoogleAuthProvider();
  provider.addScope("email");

  const result = await firebase.auth().signInWithPopup(provider);

  if (!result.user) {
    throw new Error("Login failed: No user returned");
  }

  return result.user;
};

export const sendEmailSignInLink = async (email: string): Promise<void> => {
  const firebase = await getFirebase();
  const actionCodeSettings = {
    url: window.location.origin + "/auth/email-callback",
    handleCodeInApp: true,
  };
  await firebase.auth().sendSignInLinkToEmail(email, actionCodeSettings);
  window.localStorage.setItem("emailForSignIn", email);
};

export const completeEmailSignIn = async (): Promise<firebase.default.User> => {
  const firebase = await getFirebase();
  if (!firebase.auth().isSignInWithEmailLink(window.location.href)) {
    throw new Error("Invalid email sign-in link");
  }
  const email = window.localStorage.getItem("emailForSignIn");
  if (!email) {
    throw new Error("Email not found. Please try signing in again.");
  }
  const result = await firebase.auth().signInWithEmailLink(email, window.location.href);
  window.localStorage.removeItem("emailForSignIn");
  if (!result.user) {
    throw new Error("Login failed: No user returned");
  }
  return result.user;
};

export const logoutFromFirebase = async (): Promise<void> => {
  const firebase = await getFirebase();
  await firebase.auth().signOut();
};

export const updateUserProfile = async (profile: UserProfile): Promise<void> => {
  const firebase = await getFirebase();
  await firebase.firestore().collection("users").doc(profile.uid).set(profile);
};

export const saveUserProfile = async (user: firebase.default.User): Promise<UserProfile> => {
  const firebase = await getFirebase();

  // Fetch existing profile or create default
  const doc = await firebase.firestore().collection("users").doc(user.uid).get();

  const existingProfile = doc.data() || { role: "client" };

  const updatedProfile: UserProfile = {
    ...existingProfile,
    uid: user.uid,
    name: user.displayName,
    email: user.email,
    photoUrl: user.photoURL,
    role: existingProfile.role || "client",
  };

  await updateUserProfile(updatedProfile);

  return updatedProfile;
};
